import React, { useRef, useEffect } from 'react';
import { Box, Avatar, Typography, Chip } from '@mui/material';
import { AutoAwesome as SparkleIcon, Circle as CircleIcon } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';

// Confetti particle colours
const CONFETTI_COLORS = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD'];

// Deterministic pseudo-random from an index
const lerp = (i, scale) => (((i * 2654435761) >>> 0) % 1000) / 1000 * scale;

const PARTICLES = Array.from({ length: 20 }, (_, i) => ({
  id: i,
  color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
  x: (lerp(i * 3, 320) - 160),          // spread ±160 px
  y: -(lerp(i * 7, 180) + 40),           // fly up 40–220 px
  rotate: lerp(i * 11, 720) - 360,       // spin ±360°
  size: lerp(i * 5, 8) + 5,              // 5–13 px
  delay: lerp(i * 13, 0.3),              // stagger 0–0.3 s
  isSquare: i % 3 !== 0,
}));

function ConfettiBurst() {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        pointerEvents: 'none',
        zIndex: 10,
      }}
    >
      {PARTICLES.map((p) => (
        <motion.div
          key={p.id}
          initial={{ x: 0, y: 0, opacity: 1, rotate: 0, scale: 1 }}
          animate={{
            x: p.x,
            y: p.y,
            opacity: 0,
            rotate: p.rotate,
            scale: 0.4,
          }}
          transition={{
            duration: 0.9,
            delay: p.delay,
            ease: [0.22, 1, 0.36, 1],
          }}
          style={{
            position: 'absolute',
            width: p.size,
            height: p.isSquare ? p.size : p.size * 0.6,
            borderRadius: p.isSquare ? 2 : '50%',
            backgroundColor: p.color,
          }}
        />
      ))}
    </Box>
  );
}

export default function VerificationBonusNotificationCard({ notification, onMarkRead }) {
  const payload = notification.payload || {};
  const credits = payload.credits ?? 0;
  const isUnread = !notification.is_read;

  // Fire confetti only once, on first mount while still unread
  const hasShownBurst = useRef(false);
  const showBurst = isUnread && !hasShownBurst.current;
  if (showBurst) hasShownBurst.current = true;

  const handleClick = () => {
    if (isUnread && onMarkRead) onMarkRead();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      <Box
        onClick={handleClick}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderBottom: '1px solid',
          borderColor: 'divider',
          cursor: 'pointer',
          background: isUnread
            ? 'linear-gradient(135deg, #fffbea 0%, #fff8e1 60%, #fef3c7 100%)'
            : 'transparent',
          transition: 'background 0.4s',
          '&:hover': { background: 'rgba(0,0,0,0.03)' },
        }}
      >
        {/* Shimmer stripe for unread */}
        {isUnread && (
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: '200%' }}
            transition={{ duration: 1.4, delay: 0.2, ease: 'easeInOut' }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '40%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(255,215,0,0.18), transparent)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
        )}

        {/* Left accent bar */}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            background: 'linear-gradient(180deg, #FFD700, #FFA500)',
            borderRadius: '4px 0 0 4px',
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.5, pl: 2.5, pr: 2, position: 'relative', zIndex: 2 }}>
          {/* Confetti origin + avatar */}
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <AnimatePresence>
              {showBurst && <ConfettiBurst />}
            </AnimatePresence>

            <motion.div
              animate={isUnread ? { scale: [1, 1.15, 1] } : {}}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                  color: '#fff',
                  fontSize: '1.1rem',
                  boxShadow: isUnread ? '0 0 0 3px rgba(255,215,0,0.35)' : 'none',
                  transition: 'box-shadow 0.4s',
                }}
              >
                🎉
              </Avatar>
            </motion.div>
          </Box>

          {/* Body */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.4 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}>
                You received {credits} welcome credit{credits !== 1 ? 's' : ''} when your organization membership was verified.
              </Typography>
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', lineHeight: 1.5, mb: 0.75 }}
            >
              Use your credits with the Otable AI writing assistant to accelerate your research proposals.
            </Typography>

            <Chip
              icon={<SparkleIcon sx={{ fontSize: '0.9rem !important' }} />}
              label={`${credits} AI credit${credits !== 1 ? 's' : ''} added to your account`}
              size="small"
              sx={{
                background: 'linear-gradient(90deg, #FFF3CD, #FFE082)',
                border: '1px solid rgba(255, 160, 0, 0.4)',
                color: '#7a4f00',
                fontWeight: 600,
                fontSize: '0.7rem',
              }}
            />
          </Box>

          {/* Timestamp + unread dot */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0, mt: 0.25 }}>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {notification.created
                ? formatDistanceToNow(new Date(notification.created), { addSuffix: true })
                : ''}
            </Typography>
            {isUnread && (
              <CircleIcon sx={{ fontSize: 8, color: '#FFA500' }} />
            )}
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
}
